import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  FileText, CheckCircle, XCircle, AlertCircle, Plus, 
  Calendar, Shield, ChevronRight, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FORM_TYPE_ICONS = {
  medical: '🏥',
  photo_video: '📸',
  training: '🏃',
  data_processing: '🔒',
  transport: '🚗'
};

export default function ConsentStatus({ childId, childName, childEmail, parentEmail }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingForm, setSendingForm] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/consent-forms`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load consent status:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sendConsentForm = async (formType) => {
    if (!childEmail && !parentEmail) {
      toast.error('No email address available. Please add parent/player email first.');
      return;
    }
    
    setSendingForm(formType);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/consent-forms/send-to-parent`, {
        child_id: childId,
        form_type: formType,
        parent_email: parentEmail || childEmail
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success(`${formType.replace('_', ' ')} consent form sent to ${parentEmail || childEmail}`);
      loadData(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send consent form');
    } finally {
      setSendingForm(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
        <div className="animate-pulse text-zinc-400">Loading consent status...</div>
      </div>
    );
  }

  const { consent_forms = [], summary = {} } = data || {};
  const activeForms = consent_forms.filter(f => f.status === 'active');
  const hasAllRequired = summary.all_required_signed;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="consent-status">
      {/* Header */}
      <div className={`px-6 py-4 ${hasAllRequired ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-b border-green-500/30' : 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className={`h-6 w-6 ${hasAllRequired ? 'text-green-400' : 'text-amber-400'}`} />
            <div>
              <h3 className="text-lg font-bold text-white">Consent Status</h3>
              <p className={`text-sm ${hasAllRequired ? 'text-green-400/80' : 'text-amber-400/80'}`}>
                {hasAllRequired ? 'All required consents signed' : `${summary.missing_required?.length || 0} required consent(s) missing`}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/consent-forms')}
            className="bg-[#02b5ff]/20 hover:bg-[#02b5ff]/30 text-[#02b5ff] border border-[#02b5ff]/30"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Consent
          </Button>
        </div>
      </div>

      {/* Warning Banner - Clickable to view missing consents */}
      {!hasAllRequired && (
        <button
          onClick={() => navigate('/consent-forms')}
          className="w-full bg-amber-500/10 border-b border-amber-500/20 px-6 py-3 flex items-center gap-2 hover:bg-amber-500/20 transition-colors cursor-pointer text-left"
          data-testid="missing-consents-banner"
        >
          <AlertCircle className="h-4 w-4 text-amber-400" />
          <span className="text-sm text-amber-300 flex-1">
            Missing: {summary.missing_required?.map(t => t.replace('_', ' ')).join(', ')}
          </span>
          <ChevronRight className="h-4 w-4 text-amber-400" />
        </button>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-white">{activeForms.length}</div>
            <div className="text-xs text-zinc-400">Active</div>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-white">{summary.required_types?.length || 3}</div>
            <div className="text-xs text-zinc-400">Required</div>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
            <div className={`text-2xl font-bold ${hasAllRequired ? 'text-green-400' : 'text-amber-400'}`}>
              {hasAllRequired ? '✓' : summary.missing_required?.length || 0}
            </div>
            <div className="text-xs text-zinc-400">{hasAllRequired ? 'Complete' : 'Missing'}</div>
          </div>
        </div>

        {/* Active Consents */}
        {activeForms.length > 0 ? (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-zinc-300">Active Consents</h4>
            {activeForms.map((form) => (
              <div 
                key={form.id}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl hover:bg-zinc-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{FORM_TYPE_ICONS[form.form_type] || '📄'}</span>
                  <div>
                    <div className="font-medium text-white text-sm">{form.form_name}</div>
                    <div className="text-xs text-zinc-400">
                      Signed by {form.guardian_name} • {new Date(form.signed_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-zinc-400">
            <FileText className="h-12 w-12 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm">No consent forms signed yet</p>
          </div>
        )}

        {/* Required Forms Checklist */}
        {summary.required_types && (
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <h4 className="text-sm font-semibold text-zinc-300 mb-3">Required Forms</h4>
            <div className="space-y-2">
              {summary.required_types.map((type) => {
                const isSigned = activeForms.some(f => f.form_type === type);
                const isSending = sendingForm === type;
                return (
                  <div 
                    key={type}
                    className={`flex items-center justify-between p-3 rounded-lg ${isSigned ? 'bg-green-500/10' : 'bg-[#ff025f]/10'}`}
                    data-testid={`consent-type-${type}`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{FORM_TYPE_ICONS[type] || '📄'}</span>
                      <span className={`text-sm capitalize ${isSigned ? 'text-green-400' : 'text-[#ff025f]'}`}>
                        {type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSigned ? (
                        <CheckCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => sendConsentForm(type)}
                            disabled={isSending}
                            className="h-7 px-3 bg-[#ff025f] hover:bg-[#dd0050] text-white text-xs"
                            data-testid={`send-form-${type}`}
                          >
                            {isSending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Send className="h-3 w-3 mr-1" />
                                Send Form
                              </>
                            )}
                          </Button>
                          <XCircle className="h-4 w-4 text-[#ff025f]" />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {(!childEmail && !parentEmail) && (
              <p className="text-xs text-amber-400 mt-2">
                ⚠️ Add an email address to enable sending consent forms
              </p>
            )}
          </div>
        )}

        {/* View All Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/consent-forms')}
          className="w-full mt-6 rounded-xl border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          View All Consent Forms
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
